<script setup>
import { defineProps, ref, onMounted, onUnmounted, watch, computed } from 'vue'
import Logo from '@/pages/global/pdfPreview/assets/logo.png'
import PdfDownload from '@/pages/global/pdfPreview/assets/pdf_download.png'
import sensor from '@utils/saUtilsGlobal'

const isEnvProduction = process.env.NODE_ENV === 'production'

const props = defineProps({
  pdfUrl: String,
  downloadName: String,
  isRTL: {
    type: Boolean,
    default: false
  },
  // 失效页用 — 隐藏 PDF 下载按钮(无 pdfUrl 可下载)
  hideDownload: {
    type: Boolean,
    default: false
  }
})

const innerPdfUrl = ref('')
const innerDownloadName = ref('')
watch(
  () => props.pdfUrl,
  (newVal) => {
    if (newVal) {
      innerPdfUrl.value = newVal
    }
  }
)

watch(
  () => props.downloadName,
  (newVal) => {
    if (newVal) {
      innerDownloadName.value = newVal
    }
  }
)

const downloadFile = async (url, filename) => {
  if (!url) return
  fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error('Network response was not ok')
      return res.blob()
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      if (filename) {
        a.download = filename
      }
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl) // 清理内存
    })
    .catch((error) => {
      console.error('Download failed:', error)
    })
}

const handleClickDownloadPDF = () => {
  handleTrack('download')
  // throttle(downloadFile(props.pdfUrl, props.downloadName || 'file.pdf'), 1000)
}

// // 能够将任意函数改为截流函数：
// const throttle = (fn, time) => {
//   let timer = null
//   return (...args) => {
//     if (timer) return // 如果 timer 有值，则忽略本次事件（说明技能还在冷却）
//     fn.call(undefined, ...args)
//     timer = setTimeout(() => {
//       timer = null
//     }, time)
//   }
// }

// 顶部导航栏滚动
const isHidden = ref(false)
let lastScrollY = window.scrollY
const hasTrackSlide = ref(false)

function throttle(fn, delay = 100) {
  let last = 0
  return (...args) => {
    const now = Date.now()
    if (now - last >= delay) {
      last = now
      fn(...args)
    }
  }
}

const handleScroll = () => {
  if (!hasTrackSlide.value) {
    handleTrack('slide')
  }

  const currentY = window.scrollY
  if (currentY <= 0) {
    isHidden.value = false // 滚动到顶部，强制显示
  } else if (currentY > lastScrollY) {
    isHidden.value = true // 向下滚动，隐藏
  } else {
    isHidden.value = false // 向上滚动，显示
  }
  lastScrollY = currentY
  hasTrackSlide.value = true
}
const throttledScroll = throttle(handleScroll, 100)

onMounted(() => {
  window.addEventListener('scroll', throttledScroll)
})
onUnmounted(() => {
  window.removeEventListener('scroll', throttledScroll)
})

const handleClickLogo = () => {
  window.open('Http://www.timemark.com', '_blank')
}

/**
 * 上报埋点信息
 * */
const handleTrack = (clickItem) => {
  sensor.track('H5_click_page_pdf_link', {
    clickItem,
    url: props.pdfUrl
  })
}

const downloadPdfUrl = computed(() => {
  const domain =
    isEnvProduction === 'production'
      ? 'https://app.dayscamera.com'
      : 'https://app-test.dayscamera.com'

  const resUrl = new URL(`${domain}/next/workreport/pdf/download`)
  resUrl.searchParams.set('url', innerPdfUrl.value)
  resUrl.searchParams.set('fileName', innerDownloadName.value || 'file.pdf')
  return resUrl.toString()
})
</script>

<template>
  <div
    class="navbar-wrapper"
    :class="{ hide: isHidden }"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <div class="navbar-logo" @click="handleClickLogo">
      <div v-if="isRTL">Timemark</div>
      <img :src="Logo" alt="" :style="{ width: '32px', height: '32px' }" />
      <div v-if="!isRTL">Timemark</div>
    </div>
    <div v-if="!hideDownload" class="navbar-toolbar">
      <a :href="downloadPdfUrl">
        <div class="pdfDownload" @click="handleClickDownloadPDF">
          <div class="logo">
            <img
              :style="{ width: '100%', height: '100%' }"
              :src="PdfDownload"
              alt=""
            />
          </div>
          <div class="text">PDF</div>
        </div>
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.navbar-wrapper {
  box-sizing: border-box;
  width: 100vw;
  height: 52px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  transition: transform 0.3s ease;
  z-index: 999;

  .navbar-logo {
    display: flex;
    align-items: center;
    margin-left: 16px;
    gap: 8px;
    font-weight: 590;
    font-size: 17px;
    line-height: 100%;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 16px;
    }
  }

  .navbar-toolbar {
    .pdfDownload {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 75px;
      height: 32px;
      border: 0.5px solid #000000;
      border-radius: 6px;
      cursor: pointer;
      margin-right: 20px;

      .logo {
        width: 17px;
        height: 17px;
      }

      .text {
        font-weight: 400;
        line-height: 100%;
        text-align: center;
        vertical-align: middle;
        font-size: 14px;
        color: #000;

        a {
          color: #000;
        }
      }
    }
  }
}

.navbar-wrapper.hide {
  transform: translateY(-100%);
}
</style>
